import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Image, AudioFile, VideoFile, Description, MusicNote } from '@mui/icons-material';
import { fetchFileBlob, fileServeUrl } from '../services/filesApi';

export interface FileThumbnailFile {
  _id: string;
  fileType?: string;
  mimeType?: string;
  thumbnailPath?: string;
}

const DEFAULT_SIZE = 48;

/**
 * File miniature for lists: image preview, video poster/thumbnail, or type icon.
 */
const FileThumbnail: React.FC<{
  file: FileThumbnailFile;
  size?: number;
}> = ({ file, size = DEFAULT_SIZE }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isImage = file.fileType === 'image' || (file.mimeType?.startsWith('image/') ?? false);
  const isVideo = file.fileType === 'video' || (file.mimeType?.startsWith('video/') ?? false);
  const isAudio = file.fileType === 'audio' || (file.mimeType?.startsWith('audio/') ?? false);
  const isScore = file.fileType === 'score';

  useEffect(() => {
    if (!isImage || !file._id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchFileBlob(file._id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file._id, isImage]);

  const boxSx = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden' as const,
    bgcolor: 'grey.100',
  };

  if (isImage) {
    if (loading) {
      return (
        <Box sx={boxSx}>
          <CircularProgress size={size * 0.4} />
        </Box>
      );
    }
    if (error || !url) {
      return (
        <Box sx={boxSx}>
          <Image sx={{ fontSize: size * 0.5, color: 'grey.400' }} />
        </Box>
      );
    }
    return (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      />
    );
  }

  if (isVideo) {
    if (file.thumbnailPath) {
      return (
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
          <Box
            component="img"
            src={file.thumbnailPath}
            alt=""
            sx={{
              width: size,
              height: size,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
          <VideoFile
            sx={{
              position: 'absolute',
              right: 2,
              bottom: 2,
              fontSize: size * 0.3,
              color: 'common.white',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
            }}
          />
        </Box>
      );
    }
    return (
      <Box
        component="video"
        src={fileServeUrl(file._id)}
        muted
        playsInline
        preload="metadata"
        sx={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'grey.900',
        }}
      />
    );
  }

  const Icon = isAudio ? AudioFile : isScore ? MusicNote : Description;
  return (
    <Box sx={boxSx}>
      <Icon sx={{ fontSize: size * 0.55, color: 'grey.600' }} />
    </Box>
  );
};

export default FileThumbnail;
